import { useIsMobile } from '@newfront-insurance/admin-ui';
import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { useNotify } from '@newfront-insurance/next-notifications';
import { ChevronLeft, Download, ExternalLink, Loader2, MoreHorizontal, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { ArtifactPreviewContent } from './artifact-preview-content';
import { DocumentPreviewToolbarActions } from './document-preview-toolbar-actions';
import type { ArtifactPreview, ArtifactPreviewMenuItem } from './types';

const ARTIFACT_RAIL_WIDTH = 80;
const DEFAULT_PREVIEW_PANEL_WIDTH = 755;
const MIN_PREVIEW_PANEL_WIDTH = 420;
const MAX_PREVIEW_PANEL_WIDTH = 1200;
const MIN_CHAT_CONTENT_WIDTH = 360;
const PREVIEW_PANEL_WIDTH_STEP = 24;
const MAX_VISIBLE_RAIL_ARTIFACTS = 3;

function supportsFitToPane(preview: ArtifactPreview | null) {
  return preview?.fileType === 'docx' || preview?.fileType === 'pdf';
}

function clampPanelWidth(width: number, containerWidth: number) {
  const maxWidth = Math.max(
    MIN_PREVIEW_PANEL_WIDTH,
    Math.min(MAX_PREVIEW_PANEL_WIDTH, containerWidth - MIN_CHAT_CONTENT_WIDTH),
  );
  return Math.min(Math.max(width, MIN_PREVIEW_PANEL_WIDTH), maxWidth);
}

export function ArtifactPreviewPanel({
  activeArtifactId,
  artifacts,
  className,
  isCollapsed,
  onClose,
  onCollapseChange,
  onOpenArtifact,
  preview,
  previewingArtifactId,
}: {
  activeArtifactId: string | null;
  artifacts: ArtifactPreviewMenuItem[];
  className?: string;
  isCollapsed: boolean;
  onClose: () => void;
  onCollapseChange: (isCollapsed: boolean) => void;
  onOpenArtifact: (artifact: ArtifactPreviewMenuItem) => void;
  preview: ArtifactPreview | null;
  previewingArtifactId: string | null;
}) {
  const { isMobile } = useIsMobile();
  const { download, isDownloading } = useDownloadFile();
  const notify = useNotify();
  const panelRef = useRef<HTMLElement | null>(null);
  const overflowMenuRef = useRef<HTMLDivElement | null>(null);
  const [expandedPanelWidth, setExpandedPanelWidth] = useState(DEFAULT_PREVIEW_PANEL_WIDTH);
  const [fitToPaneRequestId, setFitToPaneRequestId] = useState(0);
  const [hasUserAdjustedPreviewZoom, setHasUserAdjustedPreviewZoom] = useState(false);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const previewFitKey = preview && supportsFitToPane(preview) ? `${preview.fileType}:${preview.sourceUrl}` : null;

  const getContainerWidth = useCallback(() => {
    return panelRef.current?.parentElement?.getBoundingClientRect().width ?? window.innerWidth;
  }, []);

  useEffect(() => {
    if (!isOverflowMenuOpen) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      if (!overflowMenuRef.current?.contains(target)) {
        setIsOverflowMenuOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOverflowMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOverflowMenuOpen]);

  useEffect(() => {
    if (!previewFitKey) return undefined;

    const frame = window.requestAnimationFrame(() => {
      setHasUserAdjustedPreviewZoom(false);
      setFitToPaneRequestId((requestId) => requestId + 1);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [previewFitKey]);

  if (!preview && artifacts.length === 0) return null;

  const isPreviewExpanded = preview !== null && (isMobile || !isCollapsed);
  const usesEmbeddedDocumentToolbar = preview?.fileType === 'docx' || preview?.fileType === 'pdf';
  const usesPanelDocumentToolbar = preview?.fileType === 'pptx' || preview?.fileType === 'xlsx';
  const usesDocumentToolbarChrome = usesEmbeddedDocumentToolbar || usesPanelDocumentToolbar;

  const downloadHtmlPreview = async () => {
    if (preview?.fileType !== 'html') return;

    try {
      await download(preview.fileUuid, preview.title);
    } catch {
      notify.error(`Unable to download ${preview.title}.`);
    }
  };

  const requestAutoFitToPane = () => {
    if (!hasUserAdjustedPreviewZoom) {
      setFitToPaneRequestId((requestId) => requestId + 1);
    }
  };

  const handleResizePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = expandedPanelWidth;

    const onPointerMove = (moveEvent: PointerEvent) => {
      const nextWidth = startWidth + startX - moveEvent.clientX;
      setExpandedPanelWidth(clampPanelWidth(nextWidth, getContainerWidth()));
    };

    const onPointerUp = () => {
      requestAutoFitToPane();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const updateExpandedPanelWidth = (width: number) => {
    setExpandedPanelWidth(clampPanelWidth(width, getContainerWidth()));
    requestAutoFitToPane();
  };

  const railArtifacts = [...artifacts].reverse();
  const visibleRailArtifacts = railArtifacts.slice(0, MAX_VISIBLE_RAIL_ARTIFACTS);
  const hiddenRailArtifacts = railArtifacts.slice(MAX_VISIBLE_RAIL_ARTIFACTS);
  const hiddenRailArtifactsLabel = `${hiddenRailArtifacts.length} more generated ${
    hiddenRailArtifacts.length === 1 ? 'artifact' : 'artifacts'
  }`;
  const previewingHiddenArtifact = hiddenRailArtifacts.find((artifact) => artifact.id === previewingArtifactId);
  const hiddenRailButtonLabel = previewingHiddenArtifact
    ? `Loading ${previewingHiddenArtifact.filename} in artifact preview`
    : hiddenRailArtifactsLabel;

  const artifactRail = (
    <div
      data-testid="artifact-preview-rail"
      className={cn(
        'pointer-events-none flex h-full shrink-0 flex-col items-center bg-transparent',
        isPreviewExpanded && 'absolute inset-y-0 right-full z-20',
      )}
      style={{ width: ARTIFACT_RAIL_WIDTH }}
    >
      {preview && (!isPreviewExpanded || !usesDocumentToolbarChrome) ? (
        <div className="flex h-14 w-full shrink-0 items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={isPreviewExpanded ? 'Collapse artifact preview' : 'Expand artifact preview'}
                className="pointer-events-auto flex size-8 items-center justify-center rounded-sm text-[#546a83] transition-colors hover:bg-[#f4f5f6] hover:text-[#262d46]"
                onClick={() => onCollapseChange(isPreviewExpanded)}
              >
                <ChevronLeft className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" align="center">
              {isPreviewExpanded ? 'Collapse preview' : 'Expand preview'}
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col items-center gap-4 overflow-visible px-5 py-5">
        {visibleRailArtifacts.map((artifact) => {
          const isActive = artifact.id === activeArtifactId;
          const isPreviewing = artifact.id === previewingArtifactId;

          return (
            <Tooltip key={artifact.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Open ${artifact.filename} in artifact preview`}
                  title={artifact.filename}
                  className={cn(
                    'pointer-events-auto relative flex size-[35px] items-center justify-center rounded-[4px] bg-[#f4f8fe] text-[#546a83] shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-colors',
                    'hover:bg-white hover:text-[#262d46]',
                    isActive && 'ring-1 ring-[#bec7d0]',
                  )}
                  disabled={isPreviewing}
                  onClick={() => onOpenArtifact(artifact)}
                >
                  <FileIcon filename={artifact.filename} hasBackground={false} />
                  {isPreviewing && (
                    <Loader2 className="absolute inset-0 m-auto size-4 animate-spin rounded-full bg-white/80 p-0.5 text-[#546a83]" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" align="center">
                {artifact.filename}
              </TooltipContent>
            </Tooltip>
          );
        })}
        {hiddenRailArtifacts.length > 0 ? (
          <div ref={overflowMenuRef} className="pointer-events-auto relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={hiddenRailButtonLabel}
                  aria-expanded={isOverflowMenuOpen}
                  aria-haspopup="menu"
                  title={hiddenRailButtonLabel}
                  className={cn(
                    'relative flex size-[35px] items-center justify-center rounded-[4px] bg-[#f4f8fe] text-[#546a83]',
                    'shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-colors hover:bg-white hover:text-[#262d46]',
                    isOverflowMenuOpen && 'bg-white text-[#262d46]',
                  )}
                  disabled={previewingHiddenArtifact !== undefined}
                  onClick={() => setIsOverflowMenuOpen((open) => !open)}
                >
                  {previewingHiddenArtifact ? (
                    <Loader2 className="size-4 animate-spin text-[#546a83]" />
                  ) : (
                    <MoreHorizontal className="size-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" align="center">
                {hiddenRailButtonLabel}
              </TooltipContent>
            </Tooltip>
            {isOverflowMenuOpen ? (
              <div
                aria-label="More artifacts"
                className="z-overlay absolute top-0 right-[47px] w-64 rounded-[4px] border border-[#dee2e7] bg-white p-1 shadow-[0_2px_8px_rgba(38,45,70,0.15)]"
                role="menu"
              >
                {hiddenRailArtifacts.map((artifact) => {
                  const isActive = artifact.id === activeArtifactId;
                  const isPreviewing = artifact.id === previewingArtifactId;

                  return (
                    <button
                      key={artifact.id}
                      type="button"
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-xs text-[#546a83]',
                        'hover:bg-[#f4f8fe] hover:text-[#262d46] focus:bg-[#f4f8fe] focus:text-[#262d46] focus:outline-hidden',
                        isActive && 'bg-[#f4f8fe] text-[#262d46]',
                      )}
                      disabled={isPreviewing}
                      role="menuitem"
                      onClick={() => {
                        setIsOverflowMenuOpen(false);
                        onOpenArtifact(artifact);
                      }}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center">
                        <FileIcon filename={artifact.filename} hasBackground={false} />
                      </span>
                      <span className="min-w-0 flex-1 truncate">{artifact.filename}</span>
                      {isPreviewing ? <Loader2 className="size-3 shrink-0 animate-spin text-[#546a83]" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <aside
      ref={panelRef}
      aria-label={isPreviewExpanded ? 'Artifact preview' : 'Artifact list'}
      className={cn(
        isPreviewExpanded
          ? isMobile
            ? 'z-modal fixed inset-0 flex h-[100dvh] w-[100dvw] min-w-0 overflow-hidden'
            : 'relative flex h-full min-w-0 shrink-0 overflow-visible'
          : 'pointer-events-none absolute inset-y-0 right-0 z-20 flex h-full overflow-visible',
        isPreviewExpanded ? 'bg-white' : 'bg-transparent',
        className,
      )}
      style={
        isPreviewExpanded
          ? isMobile
            ? undefined
            : {
                width: expandedPanelWidth,
                minWidth: MIN_PREVIEW_PANEL_WIDTH,
                maxWidth: `calc(100% - ${MIN_CHAT_CONTENT_WIDTH}px)`,
              }
          : { width: ARTIFACT_RAIL_WIDTH }
      }
    >
      <TooltipProvider delayDuration={0}>
        {isPreviewExpanded && !isMobile && artifactRail}
        {isPreviewExpanded && !isMobile && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Resize artifact preview"
                aria-orientation="vertical"
                aria-valuemax={MAX_PREVIEW_PANEL_WIDTH}
                aria-valuemin={MIN_PREVIEW_PANEL_WIDTH}
                aria-valuenow={Math.round(expandedPanelWidth)}
                className="absolute top-1/2 z-10 flex h-11 w-4 -translate-x-1/2 -translate-y-1/2 cursor-col-resize items-center justify-center rounded-full"
                role="separator"
                style={{ left: 0 }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    updateExpandedPanelWidth(expandedPanelWidth + PREVIEW_PANEL_WIDTH_STEP);
                  }
                  if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    updateExpandedPanelWidth(expandedPanelWidth - PREVIEW_PANEL_WIDTH_STEP);
                  }
                  if (event.key === 'Home') {
                    event.preventDefault();
                    updateExpandedPanelWidth(MIN_PREVIEW_PANEL_WIDTH);
                  }
                  if (event.key === 'End') {
                    event.preventDefault();
                    updateExpandedPanelWidth(MAX_PREVIEW_PANEL_WIDTH);
                  }
                }}
                onPointerDown={handleResizePointerDown}
              >
                <span className="block h-[27px] w-2 rounded-full border border-[#aeb8c4] bg-white shadow-[0_1px_5px_rgba(38,45,70,0.24)]" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Resize preview</TooltipContent>
          </Tooltip>
        )}
        {isPreviewExpanded && (
          <div className={cn('flex min-w-0 flex-1 flex-col', !isMobile && 'border-l border-[#dee2e7]')}>
            {usesPanelDocumentToolbar && (
              <div
                className="grid h-12 shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,40%)_minmax(0,1fr)] items-center gap-3 overflow-hidden border-b border-[#dee2e7] bg-[#fafbfd] px-3 py-1"
                data-testid="artifact-preview-document-toolbar"
              >
                <div aria-hidden="true" />
                <div className="min-w-0 truncate text-center text-xs font-medium text-[#546a83]" title={preview.title}>
                  {preview.title}
                </div>
                <div className="ml-auto flex min-w-0 items-center justify-end gap-1.5">
                  <DocumentPreviewToolbarActions fileUrl={preview.fileUrl} onClose={onClose} />
                </div>
              </div>
            )}
            {!usesDocumentToolbarChrome && (
              <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-[#dee2e7] bg-white px-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="truncate text-xs font-semibold text-[#546a83]">{preview.title}</div>
                  {preview.fileType === 'html' ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Download preview file"
                          aria-busy={isDownloading}
                          aria-disabled={isDownloading}
                          className={cn(
                            'flex size-6 shrink-0 items-center justify-center rounded-sm text-[#546a83] transition-colors',
                            'hover:bg-[#f4f5f6] hover:text-[#262d46] disabled:cursor-progress disabled:opacity-80',
                          )}
                          data-testid="artifact-preview-download-button"
                          disabled={isDownloading}
                          onClick={() => {
                            void downloadHtmlPreview();
                          }}
                        >
                          {isDownloading ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Download className="size-4" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Download file</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={preview.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Download preview file"
                          className="flex size-6 shrink-0 items-center justify-center rounded-sm text-[#546a83] transition-colors hover:bg-[#f4f5f6] hover:text-[#262d46]"
                        >
                          <Download className="size-4" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>Download file</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {preview.fileType !== 'html' ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={preview.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open preview file in a new tab"
                          className="flex size-6 items-center justify-center rounded-sm text-[#546a83] transition-colors hover:bg-[#f4f5f6] hover:text-[#262d46]"
                        >
                          <ExternalLink className="size-4" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>Open file in new tab</TooltipContent>
                    </Tooltip>
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="Close preview"
                        className="flex size-6 items-center justify-center rounded-sm text-[#546a83] transition-colors hover:bg-[#f4f5f6] hover:text-[#262d46]"
                        onClick={onClose}
                      >
                        <X className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Close</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-auto bg-[#fafbfd]" data-testid="artifact-preview-content">
              <ArtifactPreviewContent
                fileUrl={preview.fileUrl}
                fitToPaneRequestId={fitToPaneRequestId}
                onClose={onClose}
                onUserZoomChange={() => setHasUserAdjustedPreviewZoom(true)}
                preview={preview}
              />
            </div>
          </div>
        )}
        {!isPreviewExpanded ? artifactRail : null}
      </TooltipProvider>
    </aside>
  );
}
